import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useAuthContext } from '@/contexts/AuthContext';

interface NewItemsCounts {
  projects: number;
  tasks: number;
  taskNotes: number;
  files: number;
  team: number;
  calendar: number;
  contacts: number;
  bookings: number;
  marketing: number;
  insurance: number;
  email: number;
}

type SectionKey = 'projects' | 'tasks' | 'taskNotes' | 'files' | 'team' | 'calendar' | 'contacts' | 'bookings' | 'marketing' | 'insurance' | 'email';

export const useNewItemsIndicator = () => {
  const { user, initialized } = useAuthContext();
  const [counts, setCounts] = useState<NewItemsCounts>({
    projects: 0,
    tasks: 0,
    taskNotes: 0,
    files: 0,
    team: 0,
    calendar: 0,
    contacts: 0,
    bookings: 0,
    marketing: 0,
    insurance: 0,
    email: 0,
  });
  const [loading, setLoading] = useState(true);
  const [deferred, setDeferred] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const timestampsRef = useRef<Record<string, string>>({});

  // Defer initial fetch to not block first paint
  useEffect(() => {
    const timer = setTimeout(() => setDeferred(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  // Fetch stored timestamps from database
  const fetchStoredTimestamps = useCallback(async (uid: string): Promise<Record<string, string>> => {
    try {
      const { data, error } = await supabase
        .from('user_viewed_sections')
        .select('section_key, last_viewed_at')
        .eq('user_id', uid);

      if (error) {
        console.error('Error fetching viewed sections:', error);
        return {};
      }

      const timestamps: Record<string, string> = {};
      data?.forEach(row => {
        timestamps[row.section_key] = row.last_viewed_at;
      });
      return timestamps;
    } catch (error) {
      console.error('Error fetching viewed sections:', error);
      return {};
    }
  }, []);

  // Save timestamp to database - silently handles errors
  const saveTimestamp = useCallback(async (uid: string, sectionKey: string, timestamp: string) => {
    try {
      const { error } = await supabase
        .from('user_viewed_sections')
        .upsert(
          { 
            user_id: uid, 
            section_key: sectionKey, 
            last_viewed_at: timestamp 
          },
          { 
            onConflict: 'user_id,section_key',
            ignoreDuplicates: false 
          }
        );

      // Silently ignore all errors - this is non-critical UI state
      if (error && !error.message?.includes('AbortError') && !error.message?.includes('Load failed')) {
        console.warn('Viewed section save skipped:', sectionKey);
      }
    } catch {
      // Silently ignore - network aborts and load failures are expected
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    if (!user) {
      setCounts({ projects: 0, tasks: 0, taskNotes: 0, files: 0, team: 0, calendar: 0, contacts: 0, bookings: 0, marketing: 0, insurance: 0, email: 0 });
      setLoading(false);
      return;
    }

    try {
      // Fetch timestamps from database
      const timestamps = await fetchStoredTimestamps(user.id);
      timestampsRef.current = timestamps;
      
      const defaultTimestamp = '1970-01-01T00:00:00Z';

      // Parallelize all count queries
      const [newProjects, newTasks, newTaskNotes, newFiles, newMembers, newCalendar, newContacts, newBookings, newMarketing, newInsurance] = await Promise.all([
        supabase.from('projects').select('id').gt('created_at', timestamps.projects || defaultTimestamp).neq('created_by', user.id),
        supabase.from('tasks').select('id').gt('created_at', timestamps.tasks || defaultTimestamp).neq('created_by', user.id),
        supabase.from('task_notes').select('id').gt('created_at', timestamps.taskNotes || defaultTimestamp).neq('created_by', user.id),
        supabase.from('files').select('id').gt('created_at', timestamps.files || defaultTimestamp).neq('created_by', user.id),
        supabase.from('profiles').select('id').gt('created_at', timestamps.team || defaultTimestamp).neq('id', user.id),
        supabase.from('calendar_events').select('id').gt('created_at', timestamps.calendar || defaultTimestamp).neq('created_by', user.id),
        supabase.from('contacts').select('id').gt('created_at', timestamps.contacts || defaultTimestamp).neq('created_by', user.id),
        supabase.from('bookings').select('id').gt('created_at', timestamps.bookings || defaultTimestamp).neq('created_by', user.id),
        supabase.from('marketing_campaigns').select('id').gt('created_at', timestamps.marketing || defaultTimestamp).neq('created_by', user.id),
        supabase.from('project_insurances').select('id').gt('created_at', timestamps.insurance || defaultTimestamp).neq('created_by', user.id),
      ]);

      setCounts({
        projects: newProjects.data?.length || 0,
        tasks: newTasks.data?.length || 0,
        taskNotes: newTaskNotes.data?.length || 0,
        files: newFiles.data?.length || 0,
        team: newMembers.data?.length || 0,
        calendar: newCalendar.data?.length || 0,
        contacts: newContacts.data?.length || 0,
        bookings: newBookings.data?.length || 0,
        marketing: newMarketing.data?.length || 0,
        insurance: newInsurance.data?.length || 0,
        email: 0,
      });
    } catch (error) {
      console.error('Error fetching new items counts:', error);
    } finally {
      setLoading(false);
    }
  }, [user, fetchStoredTimestamps]);

  const markAsViewed = useCallback(async (section: SectionKey) => {
    if (!user) return;
    
    const now = new Date().toISOString();
    
    // Update local state immediately for responsiveness
    setCounts(prev => ({ ...prev, [section]: 0 }));
    timestampsRef.current[section] = now;
    
    // Save to database
    await saveTimestamp(user.id, section, now);
  }, [user, saveTimestamp]);

  // Mark both tasks and taskNotes as viewed
  const markTasksAsViewed = useCallback(async () => {
    if (!user) return;
    
    const now = new Date().toISOString();
    
    // Update local state immediately for responsiveness
    setCounts(prev => ({ ...prev, tasks: 0, taskNotes: 0 }));
    timestampsRef.current.tasks = now;
    timestampsRef.current.taskNotes = now;
    
    // Save both to database
    await Promise.all([
      saveTimestamp(user.id, 'tasks', now),
      saveTimestamp(user.id, 'taskNotes', now),
    ]);
  }, [user, saveTimestamp]);

  useEffect(() => {
    if (!deferred || !user || !initialized) return;
    
    fetchCounts();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('new-items-indicator')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'projects' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tasks' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'task_notes' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'files' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'profiles' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'calendar_events' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'contacts' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'bookings' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'marketing_campaigns' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'project_insurances' },
        () => fetchCounts()
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [deferred, user, initialized, fetchCounts]);

  return {
    counts,
    loading,
    markAsViewed,
    markTasksAsViewed,
    refetch: fetchCounts,
  };
};
